// WelcomeTriage: on the blank workspace's page, under the board and before
// the kinds of things the work reached, Triage in one of two views, named
// side by side in its caption. The queue (the default) is what a standard
// flagged for a person to look at, as the Reviews lens's Triage tab lists it
// (TriageQueue): by day, the newest first, each opening to its session and
// its findings, and to a chat that investigates it. Cases (CaseInbox) are
// what needs a person joined across sessions, a case clicked opening as a
// workspace, with how much of what the standards let through a person should
// have seen. Both views read what the page's search reaches in the page's
// range, as one read they share, only once it comes near the view (and not
// while the board above reads), and stay read after. The view picked is kept
// for the browser tab, so a fresh session opens on the queue.
import { memo, useCallback, useMemo, useRef, useState } from "react";
import { Bar, REST_FILL } from "./FlowBoard.jsx";
import { useNear, useNearNow, useSettledScope } from "./FleetInventory.jsx";
import { CaseInboxBody } from "./CaseInbox.jsx";
import { TriageQueue, useTriageReads } from "./lenses/ReviewsBody.jsx";
import { useMyPermissions } from "../lib/permissions.js";
import { previewTraceCanvas } from "../lib/traceCanvasStore.js";
import { toApiRange } from "../lib/shell.js";

const NUM = new Intl.NumberFormat();
export const TRIAGE_PART = { id: "triage", label: "Triage" };
export const TRIAGE_VIEWS = [
  { id: "queue", label: "Triage", about: "What a standard flagged for a person to look at, the newest first" },
  { id: "cases", label: "Cases", about: "What needs a person: what a standard flagged, what an audit caught and rare acts, joined across sessions" },
];
const VIEW_KEY = "origin-welcome-triage-view-v1";
function readView() {
  try {
    return globalThis.sessionStorage?.getItem(VIEW_KEY) === "cases" ? "cases" : "queue";
  } catch {
    return "queue";
  }
}

// The section's caption names both views side by side, as the board's
// columns name their steps (FlowBoard StepSwitch): the one shown reads as the
// caption, with how many it lists; the other is faint until pointed at.
function ViewCaption({ view, onView, count, fresh = 0 }) {
  return (
    <h2 className="m-0 flex min-w-0 items-baseline gap-[10px] text-[15px] font-medium leading-[20px] text-[color:var(--ink)]" data-welcome-triage-views="">
      {TRIAGE_VIEWS.map((v) => {
        const on = view === v.id;
        return (
          <span key={v.id} className="flex items-baseline gap-[8px]">
            <button
              type="button"
              aria-pressed={on}
              onClick={on ? undefined : () => onView(v.id)}
              className={`transition-opacity ${on ? "cursor-default" : "opacity-[0.45] hover:opacity-100"}`}
              data-welcome-triage-view={v.id}
            >
              {v.label}
            </button>
            {on ? <span className="text-[12px] font-normal tabular-nums text-[color:var(--muted)]" data-inventory-total="">{count == null ? "" : NUM.format(count)}</span> : null}
            {on && fresh ? <span className="text-[12px] font-normal tabular-nums text-[color:var(--ink)]" data-welcome-triage-new="">{`${NUM.format(fresh)} new`}</span> : null}
          </span>
        );
      })}
    </h2>
  );
}

// The view shown, over the one read both share.
function TriageViews({ scope, bounds, view, onCount }) {
  const caps = useMyPermissions();
  const t = useTriageReads({ scopePath: scope.scopePath, range: bounds, steer: scope.steer, caps });
  const scopeLabel = scope.steer ? "this search" : "the organization";
  // A finding's session is previewed at the finding's first cited message, as
  // the Reviews lens opens it.
  const onOpen = useCallback((finding, label) => {
    if (!finding?.session) return;
    previewTraceCanvas({ id: finding.session, title: label || "", message: finding.messages?.[0] || null });
  }, []);
  const queueCount = useCallback((n) => onCount("queue", n), [onCount]);
  const casesCount = useCallback((n, fresh) => onCount("cases", n, fresh), [onCount]);
  return view === "cases"
    ? <CaseInboxBody t={t} mode="page" scopeLabel={scopeLabel} onCount={casesCount} />
    : <TriageQueue t={t} caps={caps} scopeLabel={scopeLabel} onOpen={onOpen} onCount={queueCount} />;
}

function WelcomeTriage({ scope, range, root, hold = false, sectionRef }) {
  const boxRef = useRef(null);
  const near = useNear(boxRef, root);
  const nearNow = useNearNow(boxRef, root);
  // A new search's triage waits for the board's own reads (`hold`): the
  // board is what a click there is looking at, the triage the heaviest reads
  // on the page, and the analytics reads share a few slots. It waits too
  // while the reader is away from it (scrolled up to the board), reading the
  // search when they come back near. Meanwhile it stands on the search it
  // last read.
  const [shownScope] = useSettledScope(scope, { hold, nearNow });
  // Read once it is near and the board has read; kept mounted after, so a
  // new search re-reads in place rather than starting over.
  const [live, setLive] = useState(false);
  if (!live && near && scope.ready && !hold) setLive(true);
  const [view, setView] = useState(readView);
  const pick = useCallback((next) => {
    setView(next);
    try {
      globalThis.sessionStorage?.setItem(VIEW_KEY, next);
    } catch {
      // Not kept past this page: the view still stands while it is open.
    }
  }, []);
  // How many each view lists, as it last said (null until read).
  const [counts, setCounts] = useState({ queue: null, cases: null, casesNew: 0 });
  const onCount = useCallback((id, n, fresh = 0) => setCounts((cur) => {
    if (cur[id] === n && (id !== "cases" || cur.casesNew === fresh)) return cur;
    return { ...cur, [id]: n, ...(id === "cases" ? { casesNew: fresh || 0 } : {}) };
  }), []);
  const count = counts[view];
  const setRefs = useCallback((el) => {
    boxRef.current = el;
    sectionRef(TRIAGE_PART.id, el);
  }, [sectionRef]);
  // The reads take a range as bounds; a preset's are fixed for the day.
  const rangeKey = typeof range === "string" ? range : JSON.stringify(range ?? null);
  const bounds = useMemo(() => toApiRange(range), [rangeKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const about = TRIAGE_VIEWS.find((v) => v.id === view).about;
  return (
    <section ref={setRefs} className="relative" aria-label={TRIAGE_PART.label} data-inventory-section={TRIAGE_PART.id} data-welcome-triage-shown={view}>
      {/* Its head as every section's on the page, its caption naming both
          views; the view in a box of its own. */}
      <header className="flex min-w-0 items-end gap-[12px] pb-[10px]">
        <span className="grid min-w-0 gap-[2px]">
          <ViewCaption view={view} onView={pick} count={count} fresh={view === "cases" ? counts.casesNew : 0} />
          <span className="truncate text-[12px] leading-[16px] text-[color:var(--muted)]">{about}</span>
        </span>
      </header>
      <div className="rounded-[2px] px-[12px] pb-[12px]" style={{ background: REST_FILL }} data-welcome-triage="">
        {live ? (
          <TriageViews scope={shownScope} bounds={bounds} view={view} onCount={onCount} />
        ) : (
          <div className="grid gap-[10px] py-[14px]" aria-busy="true">
            {[52, 70, 44].map((w, i) => <Bar key={i} w={`${w}%`} />)}
          </div>
        )}
      </div>
    </section>
  );
}

// Drawn again for its own sake, not each time the page is (its width
// changing as a column's seam is dragged).
export default memo(WelcomeTriage);
